import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { SearchModality } from '../feature/search-table-company/hooks/types';
import { useModalityChangeCounterStore } from '../store/modality-change-counter';
import {
  useScopedSearchParam,
  useSetScopedSearchParam,
} from './use-obtain-search-type-from-route';

/**
 * Reads / writes the current search table's `modality` (Total / Net New / Saved).
 *
 * Store-backed via useScopedSearchParam / useSetScopedSearchParam, so scope comes
 * from the pathname, NOT the SearchTableScope *context* — this resolves ANYWHERE,
 * including providers mounted at router root, above the scope provider. Reading
 * through the context there yielded `undefined` scope and silently fell back to
 * TOTAL, which left modality-gated save actions in the request payload (e.g.
 * TableActionProvider building `actions` via getSaveRecordsActions:
 * CONTACT_REVERIFY_EMAIL was sent on the Net New tab, CONTACT_REVEAL_EMAIL on
 * Saved). Same rule as useSearchSimilarityFromRoute / useSearchModeFromRoute.
 *
 * The context provider derives its own value from the same route helper, so
 * every in-tree consumer resolves identically to before.
 */
export function useSearchModality() {
  const storedModality = useScopedSearchParam<SearchModality>('modality');
  const setScopedSearchParam = useSetScopedSearchParam();
  const { incrementCounter, counter } = useModalityChangeCounterStore();
  const queryClient = useQueryClient();

  const setModality = useCallback(
    (modality: SearchModality) => {
      setScopedSearchParam('modality', modality);

      // =======================================================
      // below code is here in order to refetch search queries on each modality change
      // step 1: get all cached queries for the given modality
      const cachedQueries = queryClient.getQueryCache().findAll({
        stale: false,
        predicate: (query) => query.queryKey.includes(counter[modality]),
      });

      // step 2: if there are cached queries, increment the counter (so the cached query will be refetched)
      // if there are no cached queries, the query will be refetched anyway
      if (cachedQueries.length !== 0) {
        incrementCounter(modality);
      }
      // =======================================================
    },
    [setScopedSearchParam, incrementCounter, counter, queryClient]
  );

  return { modality: storedModality ?? SearchModality.TOTAL, setModality };
}
